import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '@/lib/api';

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function cleanConfiguredValue(value) {
  const normalized = String(value || '').trim();
  return normalized && !normalized.startsWith('%') ? normalized : '';
}

export function getGoogleClientId() {
  const runtimeClientId = typeof window !== 'undefined' ? window.__DOGGOHUB_GOOGLE_CLIENT_ID__ : '';
  return cleanConfiguredValue(runtimeClientId) || cleanConfiguredValue(process.env.REACT_APP_GOOGLE_CLIENT_ID);
}

export function loadGoogleIdentityServices() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google sign-in is only available in the browser.'));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google);
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load Google sign-in.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Unable to load Google sign-in.'));
    document.head.appendChild(script);
  });
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  const getAuthHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  const checkAuth = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/auth/me`, {
        headers,
        withCredentials: true
      });
      setUser(res.data);
    } catch {
      setUser(null);
      localStorage.removeItem('auth_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API_URL}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const forgotPassword = async (email) => {
    const res = await axios.post(`${API_URL}/auth/forgot-password`, { email });
    return res.data;
  };

  const resetPassword = async (resetToken, password) => {
    const res = await axios.post(`${API_URL}/auth/reset-password`, { token: resetToken, password });
    return res.data;
  };

  const loginWithGoogle = useCallback(async (credential) => {
    if (!credential) throw new Error('Missing Google credential.');
    const res = await axios.post(`${API_URL}/auth/google`, { credential });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch { /* ignore */ }
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, forgotPassword, resetPassword, loginWithGoogle, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
